import React, { useEffect, useState } from 'react';

import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { DropdownMenuGroup, DropdownMenuGroupLabel } from '@buildoutinc/blueprint-react/ui/DropdownMenu';
import { Sidebar } from '@buildoutinc/blueprint-react/ui/Sidebar';

import { navIcon } from './icons';
import { matchesPath, navItemState } from './paths';
import { openSupport } from './support';
import type { OsNavItem } from './types';

const CLOSE_DELAY = 150;
const FLYOUT_SELECTOR = '.os-nav__flyout';
const OPEN_TRIGGER_SELECTOR = '.os-nav__rail [data-popup-open]';

interface ItemLinkProps {
  item: OsNavItem;
  isActive: boolean;
  isCurrent: boolean;
}

function itemLinkProps({ item, isActive, isCurrent }: ItemLinkProps) {
  return {
    'aria-current': isCurrent ? ('page' as const) : undefined,
    'aria-disabled': item.disabled || undefined,
    className: classNames({ active: isActive, disabled: item.disabled }) || undefined,
  };
}

function itemBody(item: OsNavItem, isActive: boolean) {
  const icon = navIcon(item.key, isActive);

  return (
    <>
      {icon && (
        <Sidebar.MenuLinkIcon>
          <FontAwesomeIcon icon={icon} />
        </Sidebar.MenuLinkIcon>
      )}
      <Sidebar.MenuLinkLabel>{item.label}</Sidebar.MenuLinkLabel>
    </>
  );
}

interface SubItemsProps {
  item: OsNavItem;
  currentPath: string;
}

function InlineSubItems({ item, currentPath }: SubItemsProps) {
  return (
    <div className="sidebar-group-menu">
      {item.children?.map(child => (
        <Sidebar.GroupItem
          aria-current={matchesPath(child.path, currentPath) ? 'page' : undefined}
          aria-disabled={child.disabled || undefined}
          className={child.disabled ? 'disabled' : undefined}
          isActive={matchesPath(child.path, currentPath)}
          key={child.key}
          render={<a href={child.path} />}
        >
          {child.label}
        </Sidebar.GroupItem>
      ))}
    </div>
  );
}

interface NavItemProps {
  item: OsNavItem;
  currentPath: string;
  isExpanded: boolean;
  openKey: string | null;
  onOpen: React.Dispatch<React.SetStateAction<string | null>>;
}

function NavItem({ item, currentPath, isExpanded, openKey, onOpen }: NavItemProps) {
  const { isActive, isCurrent } = navItemState(item, currentPath);
  const linkProps = itemLinkProps({ item, isActive, isCurrent });

  const handleOpenChange = (open: boolean) => {
    if (open) {
      onOpen(item.key);
      return;
    }

    onOpen(current => (current === item.key ? null : current));
  };

  const menuLink = (
    <Sidebar.MenuLink
      {...linkProps}
      isActive={isActive}
      render={<a href={item.path}>{itemBody(item, isActive)}</a>}
    />
  );

  if (!item.children?.length) {
    return <Sidebar.MenuItem>{menuLink}</Sidebar.MenuItem>;
  }

  if (isExpanded) {
    return (
      <Sidebar.MenuItem>
        {menuLink}
        <InlineSubItems currentPath={currentPath} item={item} />
      </Sidebar.MenuItem>
    );
  }

  return (
    <Sidebar.MenuItem>
      <Sidebar.Dropdown
        modal={false}
        open={openKey === item.key}
        onOpenChange={handleOpenChange}
      >
        <Sidebar.DropdownTrigger
          {...linkProps}
          nativeButton={false}
          render={<a href={item.path} />}
          onMouseEnter={() => onOpen(item.key)}
        >
          {itemBody(item, isActive)}
        </Sidebar.DropdownTrigger>

        <Sidebar.DropdownMenu
          align="start"
          className="os-nav__flyout"
          finalFocus={closeType => closeType === 'keyboard'}
          side="right"
          sideOffset={8}
        >
          <DropdownMenuGroup>
            <DropdownMenuGroupLabel className="os-nav__flyout-title">
              {item.label}
            </DropdownMenuGroupLabel>

            {item.children.map(child => (
              <Sidebar.DropdownMenuItem
                aria-current={matchesPath(child.path, currentPath) ? 'page' : undefined}
                aria-disabled={child.disabled || undefined}
                className={classNames({
                  active: matchesPath(child.path, currentPath),
                  disabled: child.disabled,
                })}
                key={child.key}
                render={<a href={child.path} />}
              >
                {child.label}
              </Sidebar.DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </Sidebar.DropdownMenu>
      </Sidebar.Dropdown>
    </Sidebar.MenuItem>
  );
}

interface RailProps {
  items: OsNavItem[];
  currentPath: string;
  isExpanded: boolean;
}

export default function Rail({ items, currentPath, isExpanded }: RailProps) {
  const [openKey, setOpenKey] = useState<string | null>(null);

  useEffect(() => {
    if (!openKey) return undefined;

    let timer: number | undefined;

    const close = () => setOpenKey(null);
    const cancelClose = () => {
      window.clearTimeout(timer);
      timer = undefined;
    };

    const closeWhenPointerLeaves = (event: PointerEvent) => {
      const target = event.target as Element | null;

      if (target?.closest?.(`${FLYOUT_SELECTOR}, ${OPEN_TRIGGER_SELECTOR}`)) {
        cancelClose();
        return;
      }

      if (!timer) timer = window.setTimeout(close, CLOSE_DELAY);
    };

    document.addEventListener('pointerover', closeWhenPointerLeaves);
    window.addEventListener('scroll', close, true);

    return () => {
      cancelClose();
      document.removeEventListener('pointerover', closeWhenPointerLeaves);
      window.removeEventListener('scroll', close, true);
    };
  }, [openKey]);

  return (
    <Sidebar className="os-nav__rail" id="os-nav-rail">
      <Sidebar.Menu>
        {items.map(item => (
          <NavItem
            currentPath={currentPath}
            isExpanded={isExpanded}
            item={item}
            key={item.key}
            openKey={openKey}
            onOpen={setOpenKey}
          />
        ))}
      </Sidebar.Menu>

      <Sidebar.Footer>
        <Sidebar.MenuLink
          render={
            <button type="button">
              <Sidebar.MenuLinkIcon>
                <FontAwesomeIcon icon={faCircleQuestion} />
              </Sidebar.MenuLinkIcon>
              <Sidebar.MenuLinkLabel>Support</Sidebar.MenuLinkLabel>
            </button>
          }
          onClick={openSupport}
        />
      </Sidebar.Footer>
    </Sidebar>
  );
}
